import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";

import { TeamMemberTaskStatus } from "@fitness-app/data-models/entities/TeamMemberTask";

import { supabase } from "~/store/initializeStore";

dayjs.extend(isoWeek);

export const TEAM_MEMBER_TASKS_DUE_TODAY_PLANNED_COUNT_QUERY_KEY = "team_member_tasks_due_today_planned_count";

export interface TeamMemberTasksDueTodayOrPlannedCount {
  count: number;
  overdueCount: number;
}

/**
 * Counts pending/in-progress tasks for a given trainee where `dueDate.date` is within:
 * - today (inclusive)
 * - end of the current ISO week (inclusive)
 * and also includes "overdue" tasks (any task with `dueDate.date < today`).
 */
export const useTeamMemberTasksDueTodayOrPlannedCount = (traineeId?: string | null) => {
  const { data, isLoading, error } = useQuery({
    queryKey: [TEAM_MEMBER_TASKS_DUE_TODAY_PLANNED_COUNT_QUERY_KEY, traineeId],
    enabled: Boolean(traineeId),
    refetchInterval: 60000, // Refetch every minute while window is focused
    refetchIntervalInBackground: false,
    queryFn: async (): Promise<TeamMemberTasksDueTodayOrPlannedCount> => {
      if (!traineeId) {
        return { count: 0, overdueCount: 0 };
      }

      const today = dayjs().startOf("day");
      const todayStr = today.format("YYYY-MM-DD");
      const weekEndStr = today.startOf("isoWeek").add(6, "day").format("YYYY-MM-DD");

      // More efficient approach: use DB-side counting via JSONB expression filters.
      // Supabase/PostgREST count is returned in the `count` field.
      const overduePromise = supabase
        .from("team_member_task")
        .select("id", { count: "exact", head: true })
        .eq("traineeId", traineeId)
        .in("status", [TeamMemberTaskStatus.Pending, TeamMemberTaskStatus.InProgress])
        .lt("dueDate->>date", todayStr);

      const dueThisWeekPromise = supabase
        .from("team_member_task")
        .select("id", { count: "exact", head: true })
        .eq("traineeId", traineeId)
        .in("status", [TeamMemberTaskStatus.Pending, TeamMemberTaskStatus.InProgress])
        .gte("dueDate->>date", todayStr)
        .lte("dueDate->>date", weekEndStr);

      const [overdueResult, dueThisWeekResult] = await Promise.all([overduePromise, dueThisWeekPromise]);

      if (overdueResult.error) throw overdueResult.error;
      if (dueThisWeekResult.error) throw dueThisWeekResult.error;

      const overdueCount = overdueResult.count ?? 0;
      const dueThisWeekCount = dueThisWeekResult.count ?? 0;

      return { count: overdueCount + dueThisWeekCount, overdueCount };
    },
  });

  return {
    count: data?.count ?? 0,
    overdueCount: data?.overdueCount ?? 0,
    isLoading,
    error,
  };
};
